/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import { callApi } from '../../utils/apiCall';
import Iconify from '../Iconify';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FORM_TYPE_COLORS = {
    MEDICAL_HISTORY:       '#855CF8',
    COMMUNICATION_CONSENT: '#078DEE',
    PHOTOGRAPHY_CONSENT:   '#FF6B6B',
    RADIOGRAPHY_CONSENT:   '#FF8C00',
    SURVEY:                '#26C6DA',
    TREATMENT_PLAN:        '#00AB55',
    PATIENT_INTAKE:        '#FFAB00',
    REFERRAL:              '#FF5630',
    CONSENT:               '#637381',
    OTHER:                 '#919EAB',
};

const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const cleaned = dateStr.replace(/\.\d+/, '');
    const parsed = moment.utc(cleaned, 'DD-MM-YYYY HH:mm:ssZ');
    if (parsed.isValid()) return parsed.local();
    const iso = moment(dateStr);
    return iso.isValid() ? iso : null;
};

const formatDate = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.format('DD MMM YYYY, h:mm A') : '—';
};

/**
 * Format a field value for display based on the field type.
 * Follows the same pattern as ViewCallScript.jsx.
 */
function formatFieldValue(fieldType, uiType, value) {
    if (value === null || value === undefined || value === '') {
        return { display: null, isBlank: true };
    }

    switch (fieldType) {
        case 'boolean':
            return {
                display: value === 'yes' || value === true ? '✓ Yes' : '✗ No',
                isBlank: false,
            };

        case 'choice': {
            if (uiType === 'CHECKBOX' || uiType === 'MULTI_SELECT') {
                // Value is pipe-separated: "Option A | Option B"
                const items = typeof value === 'string' ? value.split(' | ').filter(Boolean) : (Array.isArray(value) ? value : [value]);
                return { display: items.join(', '), isBlank: items.length === 0 };
            }
            return { display: String(value), isBlank: false };
        }

        case 'datetime': {
            const m = parseApiDate(value);
            return { display: m ? m.format('DD MMM YYYY, h:mm A') : String(value), isBlank: false };
        }

        default:
            return { display: String(value), isBlank: false };
    }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Detect whether the incoming object is a ConsentRequest rather than a canonical
 * FormSubmission. The unambiguous signal is that a CR exposes a computed
 * `submission_id` field (the id of its linked PracticeFormSubmission) which is
 * distinct from its own `id`. A real submission either has no `submission_id`
 * at all, or it would equal its own `id`.
 *
 * Currently the dialog handles both shapes through fallback chains rather than
 * an explicit branch, but this helper is exported in spirit so future callers
 * can introspect a row before passing it through.
 */
// eslint-disable-next-line no-unused-vars
const isConsentRequestShape = (obj) =>
    !!obj && obj.submission_id != null && obj.id !== obj.submission_id;

/**
 * SubmissionViewerDialog — in-app modal for reviewing a completed form submission.
 *
 * Accepts either a FormSubmission (canonical) or a ConsentRequest with
 * `submission_id`. Sequence-stepper rows in the per-lead Forms tab are
 * ConsentRequests — we route them through here so they share the same modal UX
 * as standalone submissions. When a CR is passed, we resolve its linked
 * `submission_id` and fetch the submission detail via the standard endpoint
 * (`/api/v1/practices/{practiceId}/forms/{formId}/submissions/{submissionId}/`).
 *
 * Renders submitted field values directly from the `fields` array returned by the
 * submission detail API, following the same pattern as ViewCallScript.jsx.
 *
 * API response structure:
 *   fields: [{ field: { id, label, field_type: { field_type, ui_type, choices }, is_required }, value }, ...]
 */
const SubmissionViewerDialog = ({ submission, practiceId, open, onClose }) => {
    const [submissionFields, setSubmissionFields] = useState(null);
    const [loading, setLoading] = useState(false);

    // Normalize so both FormSubmission and ConsentRequest shapes work.
    // For a CR, `submission_id` points at the linked PracticeFormSubmission's id;
    // for a canonical submission, we fall back to its own id.
    const formId = submission?.form?.id || submission?.form_id;
    const submissionId = submission?.submission_id || submission?.id;
    const formName = submission?.form?.name || 'Consent Form';
    const formType = submission?.form?.form_type || 'CONSENT';
    const typeColor = FORM_TYPE_COLORS[formType] || '#637381';
    // CRs don't have `submitted_at`; their `updated_at` lands at submit time.
    const submittedAt = submission?.submitted_at || submission?.created_at || submission?.updated_at;
    const pdfUrl = submission?.pdf_url;
    const signerName = submission?.submitted_by_name
        || (submission?.lead
            ? `${submission.lead.first_name || ''} ${submission.lead.last_name || ''}`.trim()
            : '');

    // True when we have no submission id to fetch AND no pre-fetched fields —
    // most common case is a CR whose `submission_id` is null (status != SUBMITTED).
    const hasPrefetchedFields = Array.isArray(submission?.fields) && submission.fields.length > 0;
    const submissionUnavailable = !submissionId && !hasPrefetchedFields;

    // ── Load submission fields ───────────────────────────────────────────────
    useEffect(() => {
        if (!open || !submission) return;

        // 1. Fields array already present on the submission object (pre-fetched)
        if (hasPrefetchedFields) {
            setSubmissionFields(submission.fields);
            return;
        }

        // 2. Fetch from API. If we don't have enough to resolve the detail URL
        //    (e.g. a CR with a null `submission_id`), short-circuit — the body
        //    will render the "Submission details not available yet." empty state.
        if (!practiceId || !formId || !submissionId) return;

        setLoading(true);
        callApi(
            `/api/v1/practices/${practiceId}/forms/${formId}/submissions/${submissionId}/`
        ).then((res) => {
            if (res.status === 200 && res.data) {
                if (Array.isArray(res.data.fields) && res.data.fields.length > 0) {
                    setSubmissionFields(res.data.fields);
                }
            }
        }).finally(() => setLoading(false));
    }, [open, submissionId]);

    // ── Reset on close ─────────────────────────────────────────────────────
    const handleClose = () => {
        setSubmissionFields(null);
        onClose();
    };

    if (!submission) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: '14px',
                    overflow: 'hidden',
                    maxHeight: '92vh',
                },
            }}
        >
            {/* ── Header ── */}
            <Box
                sx={{
                    px: 3, py: 2,
                    bgcolor: alpha(typeColor, 0.05),
                    borderBottom: `1px solid ${alpha(typeColor, 0.15)}`,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backdropFilter: 'blur(6px)',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    {/* Form icon */}
                    <Box
                        sx={{
                            width: 42, height: 42, borderRadius: '10px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(typeColor, 0.12),
                        }}
                    >
                        <Iconify icon="solar:document-text-bold-duotone" width={22} sx={{ color: typeColor }} />
                    </Box>

                    {/* Form info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.3 }}>
                                {formName}
                            </Typography>
                            <Chip
                                label={formType.replace(/_/g, ' ')}
                                size="small"
                                sx={{
                                    fontSize: '10px', height: 18, px: 0.5, fontWeight: 700,
                                    bgcolor: alpha(typeColor, 0.1),
                                    color: typeColor,
                                    border: `1px solid ${alpha(typeColor, 0.2)}`,
                                }}
                            />
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
                            {submittedAt && (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Iconify icon="eva:calendar-fill" width={12} sx={{ color: 'text.disabled' }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                                        {formatDate(submittedAt)}
                                    </Typography>
                                </Stack>
                            )}
                            {signerName && (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Iconify icon="eva:person-fill" width={12} sx={{ color: '#00AB55' }} />
                                    <Typography variant="caption" sx={{ color: '#00AB55', fontSize: '12px', fontWeight: 600 }}>
                                        Signed by {signerName}
                                    </Typography>
                                </Stack>
                            )}
                            <Chip
                                icon={<Iconify icon="eva:checkmark-circle-2-fill" width={11} sx={{ color: '#00AB55 !important', ml: '6px !important' }} />}
                                label="Submitted"
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20, fontWeight: 700,
                                    bgcolor: 'rgba(0,171,85,0.1)', color: '#00AB55',
                                    '& .MuiChip-icon': { ml: '6px' },
                                }}
                            />
                        </Stack>
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        {pdfUrl && (
                            <Tooltip title="Download PDF">
                                <IconButton
                                    size="small"
                                    onClick={() => window.open(pdfUrl, '_blank', 'noopener noreferrer')}
                                    sx={{ color: 'text.secondary', '&:hover': { color: '#078DEE' } }}
                                >
                                    <Iconify icon="eva:download-fill" width={18} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
                                <Iconify icon="eva:close-fill" width={20} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Box>

            {/* ── Body ── */}
            <DialogContent sx={{ p: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <CircularProgress size={32} sx={{ color: typeColor }} />
                    </Box>
                ) : submissionFields && submissionFields.length > 0 ? (
                    /* Render fields directly from the fields array — same pattern as ViewCallScript */
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2.5,
                                pb: 2,
                                borderBottom: '1px solid rgba(145,158,171,0.16)',
                            }}
                        >
                            <Box
                                sx={{
                                    width: 32, height: 32, borderRadius: '8px',
                                    bgcolor: 'rgba(0,171,85,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#00AB55">
                                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
                                </svg>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    Submitted Answers
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {submissionFields.length} field{submissionFields.length !== 1 ? 's' : ''} submitted
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ borderRadius: '10px', border: '1px solid rgba(145,158,171,0.16)', overflow: 'hidden' }}>
                            {submissionFields.map((item, idx) => {
                                const fieldData = item?.field;
                                if (!fieldData) return null;

                                const fieldType = fieldData?.field_type?.field_type || '';
                                const uiType = fieldData?.field_type?.ui_type || '';
                                const label = fieldData?.label || `Field ${fieldData?.id}`;
                                const isRequired = fieldData?.is_required;
                                const { display, isBlank } = formatFieldValue(fieldType, uiType, item.value);

                                // Skip display-only label fields with no value
                                if (fieldType === 'label' && isBlank) return null;

                                return (
                                    <Box key={fieldData.id || idx}>
                                        <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.25, alignItems: 'flex-start' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    flex: '0 0 38%',
                                                    maxWidth: '38%',
                                                    color: 'text.secondary',
                                                    fontSize: '0.8125rem',
                                                    lineHeight: 1.5,
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {label}
                                                {isRequired && (
                                                    <Box component="span" sx={{ color: '#FF4842', ml: 0.25 }}>*</Box>
                                                )}
                                            </Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {isBlank ? (
                                                    <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8125rem' }}>
                                                        Not provided
                                                    </Typography>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: '0.8125rem',
                                                            fontWeight: 500,
                                                            color: 'text.primary',
                                                            lineHeight: 1.5,
                                                            wordBreak: 'break-word',
                                                            whiteSpace: 'pre-wrap',
                                                        }}
                                                    >
                                                        {display}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {idx < submissionFields.length - 1 && (
                                            <Divider sx={{ borderColor: 'rgba(145,158,171,0.1)' }} />
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ) : submissionUnavailable ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Iconify icon="eva:clock-outline" width={36} sx={{ color: 'text.disabled', mb: 1.5 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Submission details not available yet.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Iconify icon="eva:alert-triangle-fill" width={36} sx={{ color: '#FFAB00', mb: 1.5 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            No submitted data available
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            The submission data could not be loaded.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {/* ── Footer ── */}
            <Divider />
            <Box
                sx={{
                    px: 3, py: 1.5,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    bgcolor: '#fafafa',
                }}
            >
                {pdfUrl && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="eva:download-fill" width={14} />}
                        onClick={() => window.open(pdfUrl, '_blank', 'noopener noreferrer')}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            borderColor: '#DFE3E8',
                            color: '#637381',
                        }}
                    >
                        Download PDF
                    </Button>
                )}
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleClose}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        bgcolor: typeColor,
                        color: '#fff',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: typeColor, filter: 'brightness(0.88)', boxShadow: 'none' },
                    }}
                >
                    Close
                </Button>
            </Box>
        </Dialog>
    );
};

// ─── PropTypes ────────────────────────────────────────────────────────────────

SubmissionViewerDialog.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    submission: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

SubmissionViewerDialog.defaultProps = {
    submission: null,
    practiceId: null,
};

export default SubmissionViewerDialog;
